// ── Design decisions ──
// Helpers puros (sin React, sin supabase) compartidos por todas las pestañas
// de la Encomienda de viajero. Todo lo que sea formato, etiqueta o cálculo
// derivado vive acá para que las pruebas no tengan que montar componentes.
//
// · fmtUsd siempre devuelve 2 decimales con separador de miles — es lo que ve
//   el operador en subtotales y en el PDF del anexo.
// · Los cálculos de FOB aceptan las claves legacy (quantity / unit_value) como
//   respaldo de lectura, según el contrato.
// · fuzzyMatchCustomers ordena por solapamiento de tokens normalizados (sin
//   acentos, minúsculas) y admite prefijos ("L." coincide con "López" y
//   "Luisa"); nunca devuelve candidatos con puntaje 0.

export const TIPO_INGRESO_OPTIONS = [
  { value: 'pasajero_encomienda', label: 'Pasajero' },
  { value: 'carga_encomienda', label: 'Carga' },
  { value: 'compra_linea', label: 'Compra en línea' },
];

export const tipoIngresoLabel = (v) =>
  TIPO_INGRESO_OPTIONS.find((o) => o.value === v)?.label || 'Sin tipo';

export const fmtUsd = (n) => {
  const num = Number(n) || 0;
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

export const itemFob = (it) => {
  if (!it) return 0;
  const cantidad = Number(it.cantidad ?? it.quantity) || 0;
  const unitario = Number(it.fob_unitario_usd ?? it.unit_value) || 0;
  return cantidad * unitario;
};

export const paqueteFob = (p) => (p?.items || []).reduce((s, it) => s + itemFob(it), 0);

export const viajeFob = (v) => (v?.paquetes || []).reduce((s, p) => s + paqueteFob(p), 0);

export const nombreCliente = (customers, id) => {
  if (!id) return null;
  const c = (customers || []).find((x) => x.id === id);
  return c ? c.full_name : null;
};

export const normalizar = (str) =>
  String(str ?? '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .trim();

const tokens = (str) =>
  normalizar(str)
    .replace(/[^a-z0-9ñ\s]/g, ' ')
    .split(/\s+/)
    .filter(Boolean);

// Cantidad de tokens útiles de un nombre — la regla de autollenado necesita
// saber cuántos tokens tenía la consulta para exigir que TODOS coincidan.
export const contarTokensNombre = (str) => tokens(str).length;

// Variante con puntaje expuesto: [{ c, score, exactTokens }] desc, donde
// exactTokens cuenta solo coincidencias exactas de token (+2), no prefijos.
// La regla conservadora de auto-asignación decide sobre estos números.
export const fuzzyMatchCustomersScored = (ownerNameRaw, customers) => {
  const q = tokens(ownerNameRaw);
  if (q.length === 0) return [];
  return (customers || [])
    .map((c) => {
      const nt = tokens(c.full_name);
      let score = 0;
      let exactTokens = 0;
      q.forEach((qt) => {
        if (nt.includes(qt)) {
          score += 2;
          exactTokens += 1;
        } else if (nt.some((t) => t.startsWith(qt))) {
          score += 1;
        }
      });
      return { c, score, exactTokens };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.c.full_name.localeCompare(b.c.full_name));
};

export const fuzzyMatchCustomers = (ownerNameRaw, customers) =>
  fuzzyMatchCustomersScored(ownerNameRaw, customers).map((x) => x.c);
